import { useCallback, useEffect, useRef, useState } from "react"

import { useAppDispatch } from "@/core/app/hooks"
import { getScannerConnected } from "@/core/app/slices/integrations/scannerConstants"
import { getScannerConnectionStatus } from "@/core/app/slices/integrations/integrationsThunkApi"
import {
  getIntegrationScannerConfig,
  isIntegrationScanner,
  type ScannerIntegrationConfig,
} from "./scannerIntegrations.config"

type Params = {
  showScannerPatients: boolean
  scannerSelect: string
  /** Per integration UI key → connected (from Redux until generalized). */
  connectedIntegrations: Record<string, boolean>
  scannerSelectData: { key: string }[]
  setShowScannerPatients: (show: boolean) => void
}

/** Connection state, suppress-after-not-connected, single-option Select edge case. Device scanners unchanged. */
export function useIntegrationScannerOptions({
  showScannerPatients,
  scannerSelect,
  connectedIntegrations,
  scannerSelectData,
  setShowScannerPatients,
}: Params) {
  const dispatch = useAppDispatch()
  /** Timestamps of last successful connection-status fetch per integration key (30s TTL). */
  const connectionCacheRef = useRef<Record<string, number>>({})
  /** Keys hidden from dropdown for this dialog session (after not-connected OK). */
  const [suppressedScanners, setSuppressedScanners] = useState(() => new Set<string>())
  const [notConnectedDialogScanner, setNotConnectedDialogScanner] = useState<string | null>(
    null,
  )

  useEffect(() => {
    if (!showScannerPatients) {
      setSuppressedScanners(new Set())
      setNotConnectedDialogScanner(null)
    }
  }, [showScannerPatients]) // reset when dialog closes

  const suppressScanner = useCallback((key: string) => {
    setSuppressedScanners((prev) => new Set(prev).add(key))
  }, [])

  const closeNotConnectedDialog = useCallback(() => {
    setNotConnectedDialogScanner(null)
  }, [])

  const isIntegrationListAllowed = useCallback(
    (key: string) =>
      isIntegrationScanner(key) &&
      connectedIntegrations[key] === true &&
      !suppressedScanners.has(key),
    [connectedIntegrations, suppressedScanners],
  )

  const shouldIncludeIntegration = useCallback(
    (config: ScannerIntegrationConfig) => {
      if (suppressedScanners.has(config.key)) {
        return false
      }
      const connected = connectedIntegrations[config.key] === true
      // Keep row visible while modal/reconcile runs so Select value isn’t orphaned.
      const keepWhileScannerDialogOpen =
        showScannerPatients && scannerSelect === config.key && !connected
      return connected || keepWhileScannerDialogOpen
    },
    [suppressedScanners, connectedIntegrations, showScannerPatients, scannerSelect],
  )

  useEffect(() => {
    if (!showScannerPatients) {
      return
    }
    // Last option was integration and user dismissed not-connected → empty list.
    if (suppressedScanners.size > 0 && scannerSelectData.length === 0) {
      setShowScannerPatients(false)
    }
  }, [
    showScannerPatients,
    suppressedScanners,
    scannerSelectData.length,
    setShowScannerPatients,
  ])

  /** Dialog open: one GET — header requests all integration ids; response carries full `connections`. */
  useEffect(() => {
    if (!showScannerPatients) {
      return
    }
    dispatch(getScannerConnectionStatus())
  }, [showScannerPatients, dispatch])

  /** Fresh check before switching Select to an integration (onChange path). */
  const checkIntegrationConnection = useCallback(
    async (integrationKey: string): Promise<boolean> => {
      const config = getIntegrationScannerConfig(integrationKey)
      if (!config) {
        return true
      }
      const cached = connectionCacheRef.current[integrationKey]
      if (cached && Date.now() - cached < 30_000) {
        return connectedIntegrations[integrationKey] === true
      }
      try {
        const result = await dispatch(getScannerConnectionStatus()).unwrap()
        connectionCacheRef.current[integrationKey] = Date.now()
        return getScannerConnected(result.connections, config.apiName)
      } catch {
        return false
      }
    },
    [dispatch, connectedIntegrations],
  )

  const onlyIntegrationKey =
    scannerSelectData.length === 1 ? scannerSelectData[0]?.key ?? "" : ""
  const integrationOnlyInDropdown =
    showScannerPatients &&
    scannerSelectData.length === 1 &&
    isIntegrationScanner(onlyIntegrationKey)

  /** No extra API call: use Redux after the dialog-open refresh. Handles Select not firing onChange when only one integration. */
  useEffect(() => {
    if (!integrationOnlyInDropdown || !onlyIntegrationKey) {
      return
    }
    if (connectedIntegrations[onlyIntegrationKey] === false) {
      setNotConnectedDialogScanner(onlyIntegrationKey)
      return
    }
    setNotConnectedDialogScanner((prev) =>
      prev === onlyIntegrationKey ? null : prev,
    )
  }, [
    integrationOnlyInDropdown,
    onlyIntegrationKey,
    connectedIntegrations,
  ])

  return {
    suppressedScanners,
    suppressScanner,
    notConnectedDialogScanner,
    setNotConnectedDialogScanner,
    closeNotConnectedDialog,
    isIntegrationListAllowed,
    shouldIncludeIntegration,
    checkIntegrationConnection,
  }
}
